/**
 * Composable pour sanitizer le HTML et prévenir les attaques XSS
 * Compatible SSR : côté serveur, retourne le HTML tel quel
 * Côté client, charge DOMPurify dynamiquement au premier appel
 */

type DOMPurifyLike = {
  sanitize: (dirty: string, config?: Record<string, unknown>) => string;
};

let purifyInstance: DOMPurifyLike | null = null;
let purifyLoaded = false;

export function useSanitizeHtml() {
  // Charger DOMPurify côté client au premier mount
  if (import.meta.client && !purifyLoaded) {
    import("dompurify").then((mod) => {
      purifyInstance = mod.default || mod;
      purifyLoaded = true;
    });
  }

  const PURIFY_CONFIG = {
    ALLOWED_TAGS: [
      "h1", "h2", "h3", "h4", "h5", "h6",
      "p", "br", "hr",
      "ul", "ol", "li",
      "blockquote", "pre", "code",
      "strong", "b", "em", "i", "u", "s", "strike",
      "a", "img", "figure", "figcaption",
      "table", "thead", "tbody", "tr", "th", "td",
      "div", "span", "sub", "sup",
    ],
    ALLOWED_ATTR: [
      "href", "src", "alt", "title", "class", "id",
      "target", "rel", "width", "height", "style",
    ],
    ADD_ATTR: ["target"],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  };

  const sanitize = (dirty: string | null | undefined): string => {
    if (!dirty) return "";
    if (!purifyInstance) return dirty; // SSR or not yet loaded — return as-is
    return purifyInstance.sanitize(dirty, PURIFY_CONFIG);
  };

  const sanitizeStrict = (dirty: string | null | undefined): string => {
    if (!dirty) return "";
    if (!purifyInstance) return dirty.replace(/<[^>]*>/g, "");
    return purifyInstance.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  };

  return { sanitize, sanitizeStrict };
}
